import React from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  Divider,
  Chip,
  CircularProgress,
  Alert,
  Paper,
  Badge,
  SvgIconProps
} from "@mui/material";
import { useGetInvitesQuery } from "../../redux/residentPortalApi";
import { format, parseISO } from 'date-fns';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import HomeIcon from '@mui/icons-material/Home';
import PersonIcon from '@mui/icons-material/Person';

interface VisitorsListProps {
  title: string;
  icon: React.ReactElement<SvgIconProps>;
  filterFunction: (invite: any) => boolean;
  emptyMessage: string;
  showBadge?: boolean;
}

const VisitorsList: React.FC<VisitorsListProps> = ({
  title,
  icon,
  filterFunction,
  emptyMessage,
  showBadge = false
}) => {
  // Get the current date in ISO format for timezone parameter
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  
  // Fetch all invites
  const { data: invites, isLoading, error } = useGetInvitesQuery({ timeZone });

  // Filter invites based on the provided filter function
  const filteredInvites = React.useMemo(() => {
    if (!invites) return [];
    return invites.filter(filterFunction);
  }, [invites, filterFunction]);

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        Error loading visitors. Please try again later.
      </Alert>
    );
  }

  return (
    <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        {showBadge ? (
          <Badge badgeContent={filteredInvites.length} color="primary" sx={{ mr: 1 }}>
            {icon}
          </Badge>
        ) : (
          <Box sx={{ mr: 1 }}>
            {icon}
          </Box>
        )}
        <Typography variant="h5" color="#345678">
          {title}
        </Typography>
      </Box>
      
      {filteredInvites.length === 0 ? (
        <Box sx={{ p: 2, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            {emptyMessage}
          </Typography>
        </Box>
      ) : (
        <List>
          {filteredInvites.map((invite: any) => {
            const isScanned = invite.scanned;
            const isRevoked = invite.revoked;
            
            return (
              <React.Fragment key={invite.id}>
                <ListItem 
                  alignItems="flex-start"
                  sx={{
                    backgroundColor: isScanned ? 'rgba(76, 175, 80, 0.1)' : 'transparent',
                    borderLeft: isScanned ? '4px solid #4caf50' : 'none',
                    pl: isScanned ? 2 : 3,
                    opacity: isRevoked ? 0.6 : 1
                  }}
                >
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                        <PersonIcon sx={{ fontSize: 20, mr: 1, color: '#345678' }} />
                        <Typography variant="subtitle1" component="span">
                          {invite.visitorName}
                        </Typography>
                        {isScanned && (
                          <Chip 
                            label={isRevoked ? "Revoked" : "Checked In"} 
                            size="small" 
                            color={isRevoked ? "error" : "success"} 
                            sx={{ ml: 1 }}
                          />
                        )}
                        {!isScanned && isRevoked && (
                          <Chip 
                            label="Revoked" 
                            size="small" 
                            color="error" 
                            sx={{ ml: 1 }}
                          />
                        )}
                      </Box>
                    }
                    secondary={
                      <Box sx={{ pl: 0.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                          <HomeIcon sx={{ fontSize: 18, mr: 1, color: 'text.secondary' }} />
                          <Typography variant="body2" color="text.secondary" component="span">
                            Property ID: {invite.propertyId}
                          </Typography>
                        </Box>
                        
                        <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                          <AccessTimeIcon sx={{ fontSize: 18, mr: 1, color: 'text.secondary' }} />
                          <Typography variant="body2" color="text.secondary" component="span">
                            {invite.isMultiDay
                              ? `From ${invite.startDate ? format(parseISO(invite.startDate), 'dd MMM yyyy') : 'N/A'} to ${invite.endDate ? format(parseISO(invite.endDate), 'dd MMM yyyy') : 'N/A'}`
                              : `Time: ${invite.visitDateTime ? format(parseISO(invite.visitDateTime), 'HH:mm') : 'N/A'}`}
                          </Typography>
                        </Box>
                        
                        {invite.visitorWhatsApp && (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            WhatsApp: {invite.visitorWhatsApp}
                          </Typography>
                        )}
                        
                        {invite.visitorEmail && (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            Email: {invite.visitorEmail}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                </ListItem>
                <Divider component="li" />
              </React.Fragment>
            );
          })}
        </List>
      )}
    </Paper>
  );
};

export default VisitorsList;